/**
 * Shared edge-bot wire: the canonical agent telemetry shapes, the trader-facing
 * mode → edge-kind mapping, client-side config resolution (personal → org →
 * built-in, mirroring the relay's resolveKindConfig), and the spawn/claim
 * helpers. Used by the Value Dashboard spawn flow, the presence markers, and the
 * Position Tracker bot zones — one source of truth for the /api/edge surface.
 */
import { apiFetch } from '@/stores/authStore';
import type { BotDefaults, BotKindConfig } from './types';
import { BOT_KIND_SPECS } from './configSpec';

// ── agent telemetry (mirrors g10edge agent.Stats; owner added relay-side) ──
export interface RestingOrder {
  id: string;
  side: 'bid' | 'ask';
  priceCents: number;
  sizeContracts: number;
  queuePosition: number; // contracts ahead; -1 = unknown
  queueSource?: string; // direct | hydra | orders | none
}
export interface MarkoutStat {
  horizonMs: number;
  fills: number;
  rightSideRate: number;
  avgCents: number;
}
export interface AgentStats {
  id: string;
  ticker: string;
  kind: string;
  mode: string;
  paused: boolean;
  posture: string; // green | yellow | orange | red
  lastFairCents: number;
  netInventory: number;
  lineVelocityCentsPerSec?: number;
  depthSource?: string;
  queueStatus?: string;
  queueStatusReason?: string;
  markout?: MarkoutStat[];
  resting: RestingOrder[] | null;
  loops: number;
  placed: number;
  canceled: number;
  errors: number;
  startedAtMs: number;
  owner?: { id: string; username: string } | null;
  /** Server-computed authz for the viewer (org-membership gated; admin role does
   *  NOT bypass). `canClaim` = may stake this bot (member of its org); `canOperate`
   *  = may pause/resume/reband/kill (member, and for a trader also the owner).
   *  Absent on older relay responses → treat as permissive fallback. */
  canClaim?: boolean;
  canOperate?: boolean;
}

export interface EdgeAgentsResponse {
  agents: AgentStats[];
  dryRun?: boolean;
}

// ── trader-facing bot modes (the Value Dashboard mode-row) → edge kind ──
export type TraderBotMode = 'vanilla' | 'recycler';
export interface TraderBotKind {
  mode: TraderBotMode;
  kind: string;
  label: string;
}
export const TRADER_BOT_KINDS: TraderBotKind[] = [
  { mode: 'vanilla', kind: 'vanillamm', label: 'Vanilla' },
  { mode: 'recycler', kind: 'queuerecycler', label: 'Recycler' },
];
export function kindForMode(mode: TraderBotMode): string {
  return TRADER_BOT_KINDS.find((k) => k.mode === mode)?.kind ?? 'vanillamm';
}

/** The contract-size field a given kind exposes in the minimal spawn form. */
export function sizeFieldForKind(kind: string): string {
  return BOT_KIND_SPECS.find((s) => s.kind === kind)?.primary[0] ?? 'sizeContracts';
}

/**
 * Resolve effective config for a kind: org profile → org byKind → personal
 * byKind (later wins). Returns only set fields; unset fall through to the edge
 * built-in. Mirrors relay/src/services/botConfig.ts resolveKindConfig.
 */
export function resolveBotConfig(
  kind: string,
  personal: BotDefaults | null | undefined,
  org: BotDefaults | null | undefined,
): BotKindConfig {
  const orgProfile = (org?.profile && org.profiles?.[org.profile]?.[kind]) || {};
  const orgByKind = org?.byKind?.[kind] || {};
  const personalByKind = personal?.byKind?.[kind] || {};
  return { ...orgProfile, ...orgByKind, ...personalByKind };
}

export interface SpawnResult {
  ok: boolean;
  status: number;
  id?: string;
  /** structured "a bot already exists here" (BOT_EXISTS) → offer claim. */
  existingId?: string;
  error?: string;
  code?: string;
}

/**
 * Spawn a bot on a market. Defaults to GUARD mode (quote_for_rebate, post-only);
 * pass mode:'cross_for_fills' to "let it ride" (take fills) from spawn. Mode is
 * an INDEPENDENT lever from claiming. `config` should already carry the resolved
 * defaults + the form's size.
 */
export async function spawnBot(args: {
  ticker: string;
  kind: string;
  config: BotKindConfig;
  intervalMs?: number;
  mode?: 'quote_for_rebate' | 'cross_for_fills';
}): Promise<SpawnResult> {
  const config: Record<string, unknown> = { ...args.config };
  // queuerecycler quotes a sides array; default to two-sided presence.
  if (args.kind === 'queuerecycler' && config.sides === undefined) {
    config.sides = ['bid', 'ask'];
  }
  try {
    const res = await apiFetch('/api/edge/agents', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ticker: args.ticker,
        kind: args.kind,
        mode: args.mode ?? 'quote_for_rebate',
        intervalMs: args.intervalMs ?? 250,
        config,
      }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      return { ok: false, status: res.status, error: data.error, code: data.code, existingId: data.existingId };
    }
    return { ok: true, status: res.status, id: data.id };
  } catch (e) {
    return { ok: false, status: 0, error: (e as Error).message };
  }
}

export interface ClaimResult {
  ok: boolean;
  /** 'ALREADY_CLAIMED' when another trader holds it — surface to the user. */
  code?: string;
  error?: string;
}

/** Claim a bot under the current trader (relay-side attribution). Returns the
 *  structured outcome (not a bare boolean) so a lost claim (409 ALREADY_CLAIMED)
 *  can be shown to the user instead of silently no-op'ing. */
export async function claimBot(id: string, ticker?: string): Promise<ClaimResult> {
  try {
    const res = await apiFetch(`/api/edge/agents/${encodeURIComponent(id)}/claim`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(ticker ? { ticker } : {}),
    });
    if (res.ok) return { ok: true };
    const data = await res.json().catch(() => ({}));
    return { ok: false, code: data.code, error: data.error };
  } catch (e) {
    return { ok: false, error: (e as Error).message };
  }
}

/** Release a claimed bot back to the org pool. */
export async function releaseBot(id: string): Promise<boolean> {
  try {
    const res = await apiFetch(`/api/edge/agents/${encodeURIComponent(id)}/release`, { method: 'POST' });
    return res.ok;
  } catch {
    return false;
  }
}
